import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { OrderIndividualFinanceMapperService } from '../../order-individual-finance-mapper.service';
import { OrderJsonModel } from 'src/app/core/models/order-model/order-json';
import { FinanceAccountionType, OrderDetailFinanceAccountingView } from '../../views/order-detail-finance-accounting.view';
import { ContentReadyEvent } from 'devextreme/ui/tree_list';
import { DateConverterService } from 'src/app/core/utils/date-converter.service';
import { orderDetailAccountingPage } from './order-detail-individual-finance-constants';
import { OrderStateCode } from 'src/app/core/constants/order-constants';
import { ProductGroupCodeConstant } from '../../order.constants';

declare var $: any;

@Component({
    selector: 'op-order-detail-individual-finance',
    templateUrl: './order-detail-individual-finance.component.html',
    providers: [
        OrderIndividualFinanceMapperService
    ]
})
export class OrderDetailIndividualFinanceComponent implements OnChanges {
    private expanded = false;
    public treeData: OrderDetailFinanceAccountingView[];
    public financeAccountionType = FinanceAccountionType;

    @Input() pageNumbers: number[];
    @Input() individualIndex: number;
    @Input() individualId: string;
    @Input() productId: string;
    @Input() orderJson: OrderJsonModel;
    @Input() panelCode: string;
    @Input() headerCurrencyCode: string;
    @Input() partnerOrderId: string;
    @Input() paymentId: string;
    @Output() onNextIndividualPage = new EventEmitter();
    @Output() onPrevIndividualPage = new EventEmitter();

    get isHeaderFinance(): boolean {
        return this.panelCode == OrderStateCode.Header;
    } 

    constructor(private orderIndividualFinanceMapperService: OrderIndividualFinanceMapperService,
        private dateConverterService: DateConverterService,
        private changeDetector: ChangeDetectorRef
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['orderJson'] && this.orderJson) {
            this.getFinanceTree();
        }
    }

    private getFinanceTree() {
        switch (this.panelCode) {
            case OrderStateCode.Individual:
                this.getIndividualFinanceTree(this.orderJson, this.individualId);
                return;
            case OrderStateCode.Product:
                this.getProducyFinanceTree(this.orderJson, this.productId);
                return;
            case ProductGroupCodeConstant.Payment:
                this.getPaymentFinanceTree(this.orderJson, this.paymentId);
                return;
            case OrderStateCode.Header:
                this.getPaymentHeaderTree(this.orderJson);
            default:
                return;
        }
    }

    onRowPrepared(e: any) {
        if (!this.expanded) return;
        this.moveToggleButtonToSecondCell(e);
    }

    public formatShortDate(date: string | Date): string {
        return this.dateConverterService.formatSystemShortDate(date);
    }

    public checkPaddingType(node: OrderDetailFinanceAccountingView): string {
        switch (node.type) {
            case FinanceAccountionType.Product:
            case FinanceAccountionType.Individual:
            case FinanceAccountionType.Refund:
            case FinanceAccountionType.Voucher:
                return 'pl-1';
            default:
                return 'pl-3';
        }
    }

    private moveToggleButtonToSecondCell(e: any) {
        if (e.rowType == 'data') {
            setTimeout(() => {
                if (!e.cells?.[0]?.cellElement) return;
                const elem$ = $(e.cells[0].cellElement);
                const firstCellHtml = elem$.find('.dx-treelist-text-content').html();
                const children$ = elem$.children().detach();
                const parent$ = elem$.parent();
                const secondCell$ = parent$.children().eq(1);
                const secondCellHtml = secondCell$.html();
                elem$.html(firstCellHtml);
                children$.first().addClass('pt-1');
                children$.last().html(secondCellHtml);
                secondCell$.html(children$);
                this.adjustDescriptionIndent(e, children$);
            })
        }
    }

    private adjustDescriptionIndent(e: any, children$: any) {
        const noSpaceType = [
            FinanceAccountionType.Balance
        ]
        if (noSpaceType.includes(e.data?.type)) {
            children$.first().find('.dx-treelist-empty-space').detach();
        } else {
            const emptySpace = $(`<div class="dx-treelist-empty-space"></div>`);
            children$.first().prepend(emptySpace);
        }
    }

    onContentReady(e: ContentReadyEvent) {
        if (this.expanded || !this.treeData) return;
        e.component.forEachNode(node => {
            e.component.expandRow(node.key)
        })
        this.expanded = true;
    }

    public getIndividualFinanceTree(orderJson: OrderJsonModel, individualId: string) {
        if (!individualId) return;
        this.treeData = this.orderIndividualFinanceMapperService.getIndividualFinanceAccounting(orderJson, individualId);
        this.changeDetector.detectChanges();
    }

    public getProducyFinanceTree(orderJson: OrderJsonModel, productId: string) {
        if (!productId) return;
        this.treeData = this.orderIndividualFinanceMapperService.getProductFinanceAccounting(orderJson, this.productId, this.partnerOrderId);
    }

    public getPaymentFinanceTree(orderJson: OrderJsonModel, paymentId: string) {
        if (!paymentId) return;
        this.treeData = this.orderIndividualFinanceMapperService.getPaymentFinanceAccounting(orderJson, this.paymentId);
    }

    public getPaymentHeaderTree(orderJson: OrderJsonModel) {
        this.treeData = this.orderIndividualFinanceMapperService.getPaymentHeaderAccounting(orderJson);
    }

    public showPreviousBtn(): boolean {
        return this.pageNumbers[this.individualIndex] > 0
    }

    public disabledNextBtn(): boolean {
        return this.pageNumbers[this.individualIndex] >= orderDetailAccountingPage.length - 1
    }
    
    public nextIndividualPage() {
        this.onNextIndividualPage.emit(this.individualIndex);
    }

    public previousIndividualPage() {
        this.onPrevIndividualPage.emit(this.individualIndex);
    }

    public getPercentDisplay(percent: string): string {
        if (!percent) return '';
        return `${percent}%`;
    }

    public getBalanceClass(balance: number) {
        balance = parseFloat(this.getBalanceString(balance));
        if (balance > 0) {
            return 'outstanding-amount-refund';
        }
        if (balance < 0) {
            return 'color-danger-500';
        }
        return 'color-success-500';
    }

    public getBalanceString(balance: number): string {
        if (balance == null) return null;
        let balanceAmountText = Math.abs(balance).toFixed(2);
        if (balance != null && parseFloat(balanceAmountText) == 0) {
            return '0.00';
        }
        return balance.toFixed(2);
    }

    public getHeaderClass(type: FinanceAccountionType): boolean {
        if (type == FinanceAccountionType.Payment 
            || type == FinanceAccountionType.Product
            || type == FinanceAccountionType.Exchange
            || type == FinanceAccountionType.Balance
            || type == FinanceAccountionType.Individual
            || type == FinanceAccountionType.CancelledPayment
            || type == FinanceAccountionType.Refund
        ) return true;
        return false;
    }

    public displayIndividualCon(data: OrderDetailFinanceAccountingView): boolean {
        return data?.type == FinanceAccountionType.Individual || !!data?.individualIconClass
    }
}
